import React, { createContext, useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser as setReduxUser, clearUser, setLoading as setReduxLoading } from "../store/slices/userSlice";
import { clearSelectedCompany } from '../store/slices/companySlice';
import toast from "../utils/notify";
import { getUserDataApi } from "../services/axios/AuthApis/UserMasterApis";
import { isModuleEnabledForTenant } from "../utils/moduleAccess";

const AuthContext = createContext();

// RBAC System - Role Definitions
const ROLES = {
  PLATFORM_ADMIN: 'platform_admin',
  CUSTOMER_ADMIN: 'customer_admin',
  SECURITY_ANALYST: 'security_analyst',
  AUDITOR: 'auditor',
  END_USER: 'end_user'
};

// Comprehensive RBAC Permission Matrix
const RBAC_PERMISSIONS = {
  // Trust Center Permissions
  'trust_center_read': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'tenant',
    end_user: false
  },
  'trust_center_edit': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: false,
    end_user: false
  },

  // Risk Management Permissions
  'risk_crud': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: false
  },

  // AI CSPM/DSPM Permissions
  // auditor: false - the original (Feb-Apr 2026) Auditor has no CSPM/DSPM access.
  'cspm_dspm_actions': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: false,
    end_user: false
  },

  // Incident Management Permissions
  'incident_crud': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: 'create_own'
  },

  // VAPT Findings Permissions
  // auditor: false - the original (Feb-Apr 2026) Auditor has no VAPT access.
  'vapt_findings': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: false,
    end_user: false
  },

  // TPRM Permissions
  'tprm': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: false
  },

  // Document Library Permissions
  'doc_library': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: 'acknowledge'
  },

  // Learning Management System Permissions
  'lms': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: 'learn_quiz'
  },

  // AI Analytics Reports Permissions
  'ai_analytics': {
    platform_admin: 'all',
    customer_admin: 'tenant',
    security_analyst: 'tenant',
    auditor: 'read',
    end_user: false
  },

  // Administration Permissions (No global settings for Security Analyst)
  'admin_users_sso_mfa': {
    platform_admin: 'all',
    customer_admin: 'tenant_own',
    security_analyst: false,  // No global settings access
    auditor: false,
    end_user: false
  }
};

// Module Access Control based on RBAC
//
// AUDITOR footprint (original Feb-Apr 2026 role, restored): the Auditor is a
// compliance/evidence reviewer and may ONLY reach:
//   dashboard (-> CentralDashboard), document-library, risk-assessment /
//   risk-management (read-only), trust-center (read-only), settings.
// Every other module - CSPM, DSPM, VAPT, Virtual SOC, security-dashboard,
// third-party-assessment, learning-management, advanced-reporting,
// asset-management, incident-reporting, team-collaboration, administration,
// ai-crm - is NOT part of the original Auditor role. Auditor support that was
// added to CSPM/DSPM/VAPT/SOC after April 2026 has been rolled back here and is
// also 403'd at the backend (shared.auditor_module_block.AuditorModuleBlockMiddleware).
const MODULE_PERMISSIONS = {
  "dashboard": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR, ROLES.END_USER],
  "trust-center": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR],
  "risk-assessment": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR, ROLES.END_USER],
  "ai-cspm": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "dspm": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "incident-reporting": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.END_USER],
  "ai-vapt": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "third-party-assessment": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  // end_user deliberately excluded - Document Library is not an end_user module
  // (also hard-denied in canAccess() and by the backend module-access gate).
  "document-library": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR],
  "learning-management": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.END_USER],
  "advanced-reporting": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "asset-management": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "ai-virtual-soc": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "security-dashboard": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "ai-crm": [ROLES.PLATFORM_ADMIN],
  "administration": [ROLES.PLATFORM_ADMIN], // No administration access for Security Analyst
  "settings": [ROLES.SECURITY_ANALYST, ROLES.AUDITOR, ROLES.END_USER, ROLES.CUSTOMER_ADMIN],
  "team-collaboration": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],

  // Legacy mappings
  "compliance-management": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR],
  "risk-management": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST, ROLES.AUDITOR],
  "threat-response": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "reports": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "integrations": [ROLES.PLATFORM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.SECURITY_ANALYST],
  "enduser-dashboard": [ROLES.END_USER],
};

// Modules an end_user must NEVER access, even if a per-user
// `employee_details.module_access_to` flag happens to be set for them.
// Mirrors the backend block in shared/module_access.py
// (END_USER_DENIED_MODULE_PREFIXES). Consumed by canAccess(), which is the
// single gate for both the sidebar (Layout.js) and <ProtectedRoute>.
const END_USER_DENIED_MODULES = ["document-library"];

// Legacy Role Mapping for Backward Compatibility
const mapLegacyRole = (role) => {
  const legacyMapping = {
    'admin': 'platform_admin',
    'superadmin': 'platform_admin',
    'company_user': 'customer_admin',
    'auditor': 'auditor',
    'end_user': 'end_user'
  };

  return legacyMapping[role] || role;
};

// RBAC Utility Functions
const checkPermission = (userRole, permission, action = null) => {
  if (!userRole || !RBAC_PERMISSIONS[permission]) {
    return false;
  }

  // Map legacy roles to new RBAC roles
  const mappedRole = mapLegacyRole(userRole);
  const permissionLevel = RBAC_PERMISSIONS[permission][mappedRole];

  if (permissionLevel === false) {
    return false;
  }

  if (permissionLevel === true || permissionLevel === 'all' || permissionLevel === 'tenant') {
    return true;
  }

  // Check specific action permissions
  if (action && typeof permissionLevel === 'string') {
    return permissionLevel.includes(action) || permissionLevel === action;
  }

  return permissionLevel;
};

const hasModuleAccess = (userRole, moduleName) => {
  if (!userRole || !MODULE_PERMISSIONS[moduleName]) {
    return false;
  }

  // Map legacy roles to new RBAC roles
  const mappedRole = mapLegacyRole(userRole);
  return MODULE_PERMISSIONS[moduleName].includes(mappedRole);
};

const canRead = (userRole, capability) => {
  return checkPermission(userRole, capability) !== false;
};

const canEdit = (userRole, capability) => {
  const permission = checkPermission(userRole, capability);
  return permission === 'all' || permission === 'tenant' || permission === true ||
    (typeof permission === 'string' && (permission.includes('create') || permission.includes('update') || permission.includes('edit')));
};

const canCreate = (userRole, capability) => {
  const permission = checkPermission(userRole, capability);
  return permission === 'all' || permission === 'tenant' || permission === true ||
    (typeof permission === 'string' && permission.includes('create'));
};

const canDelete = (userRole, capability) => {
  const permission = checkPermission(userRole, capability);
  return permission === 'all' || permission === 'tenant' || permission === true;
};

const getPermissionLevel = (userRole, capability) => {
  return checkPermission(userRole, capability);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, isAuthenticated, loading } = useSelector((state) => state.user);

  // Auth initialization for the Redux store is synchronous: redux-persist
  // has already rehydrated `user` / `isAuthenticated` behind <PersistGate>
  // by the time this provider mounts. The `loading` flag is only ever set
  // to `true` by the slice's initialState (and can also be rehydrated as a
  // stale `true`), and nothing on the logged-out path ever clears it - which
  // left <ProtectedRoute> stuck on its spinner forever for a logged-out
  // visitor. Resolve it once here so the route guards can make a stable
  // login/dashboard decision.
  useEffect(() => {
    dispatch(setReduxLoading(false));
  }, [dispatch]);

  // Refresh the user profile (role, user_menus, enabled_modules,
  // customer_requirements) from the backend on every app load / refresh
  // when already authenticated. redux-persist keeps the last `user` in
  // localStorage, so without this a customer admin could keep seeing an
  // old module list even after the backend module config changed
  // (stale-cache). A 401 here is already handled by the axios interceptor
  // (logout); any other error just leaves the persisted profile in place.
  useEffect(() => {
    if (!isAuthenticated || window.location.pathname === "/vendor-assessment") {
      return;
    }
    let cancelled = false;
    getUserDataApi()
      .then((fresh) => {
        if (!cancelled && fresh) dispatch(setReduxUser(fresh));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated]);

  // Custom setUser that uses Redux
  const setUserWithPersistence = (userData) => {
    dispatch(setReduxUser(userData));
    if (userData) {
      console.log("✅ User data saved to Redux:", userData);
    }
  };

  // Custom setIsAuthenticated is now handled by Redux automatically
  const setIsAuthenticatedWithPersistence = (authState) => {
    // This is now handled by Redux when we setUser
    // We'll keep this for backward compatibility but it doesn't need to do anything
    console.log("✅ Auth state:", authState);
  };

  const logout = () => {
    dispatch(clearUser());
    dispatch(clearSelectedCompany());
    localStorage.removeItem("enabledModules");
    toast.success("Logged out successfully");
  };

  // Access control function
  const canAccess = (moduleName) => {
    // short‑circuit when consent not given: only dashboard allowed
    const consent = localStorage.getItem("userConsent") === "true";
    if (!consent) {
      return moduleName === "dashboard" || moduleName === "settings";
    }

    if (!user || !user.role) {
      return false;
    }
    if (user.role == "end_user") {
      // Hard deny - not overridable by a per-user module_access_to flag.
      if (END_USER_DENIED_MODULES.includes(moduleName)) {
        return false;
      }
      let accessedModules = user?.employee_details?.module_access_to || [];
      return accessedModules[moduleName] === true &&
        isModuleEnabledForTenant(user, moduleName);
    }
    const allowedRoles = MODULE_PERMISSIONS[moduleName];
    if (!allowedRoles) {
      // If module is not defined in permissions, deny access by default
      return false;
    }

    // Use the mapped role for checking access
    const mappedRole = mapLegacyRole(user.role);
    const roleAllowed =
      allowedRoles.includes(mappedRole) || allowedRoles.includes(user.role);

    // Customer Admin (and tenant sub-users) additionally lose access to a
    // module whose status is DISABLED / BLOCKED / INACTIVE for their
    // organization - authoritative list comes from the backend
    // (`enabled_modules`). Platform admins are unaffected.
    return roleAllowed && isModuleEnabledForTenant(user, moduleName);
  };

  // Enhanced RBAC Functions
  const hasPermission = (permission, action = null) => {
    return checkPermission(user?.role, permission, action);
  };

  const hasModulePermission = (moduleName) => {
    return hasModuleAccess(user?.role, moduleName);
  };

  const canReadCapability = (capability) => {
    return canRead(user?.role, capability);
  };

  const canEditCapability = (capability) => {
    return canEdit(user?.role, capability);
  };

  const canCreateCapability = (capability) => {
    return canCreate(user?.role, capability);
  };

  const canDeleteCapability = (capability) => {
    return canDelete(user?.role, capability);
  };

  const getUserPermissionLevel = (capability) => {
    return getPermissionLevel(user?.role, capability);
  };

  const value = {
    user,
    loading,
    logout,
    setUser: setUserWithPersistence,
    setIsAuthenticated: setIsAuthenticatedWithPersistence,
    canAccess,
    isAuthenticated,

    // RBAC Functions
    hasPermission,
    hasModulePermission,
    canReadCapability,
    canEditCapability,
    canCreateCapability,
    canDeleteCapability,
    getUserPermissionLevel,

    // Role Constants
    ROLES,

    // User Role Helpers
    isPlatformAdmin: user?.role === ROLES.PLATFORM_ADMIN ||
      user?.role === 'admin' ||
      user?.role === 'superadmin',
    isCustomerAdmin: user?.role === ROLES.CUSTOMER_ADMIN || user?.role === 'company_user',
    isSecurityAnalyst: user?.role === ROLES.SECURITY_ANALYST,
    isAuditor: user?.role === ROLES.AUDITOR || user?.role === 'auditor',
    isEndUser: user?.role === ROLES.END_USER || user?.role === 'end_user',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};